import React, { useEffect, useState } from 'react';
import Api from '../../../services/api';
import { Row, Col, Typography, Button, Table, Modal, Input, Upload, Space, notification, Tooltip, Popconfirm } from 'antd';
import { UploadOutlined, DeleteOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';

function ReportComplements({ report, setReport }) {
    const [complements, setComplements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [description, setDescription] = useState('');
    const [fileList, setFileList] = useState([]);

    const [api, contextHolder] = notification.useNotification();
    const [deleteTooltipVisible, setDeleteTooltipVisible] = useState({});

    const fetchComplements = async () => {
        setLoading(true);

        try {
            const response = await Api(`/reports/${report.id}/complements`).index();
            setComplements(response.data);
        } catch (err) {
            api.error({ message: 'Falha ao carregar complementos.' });
        }

        setLoading(false);
    }

    useEffect(() => {
        if (report) fetchComplements();
    }, [report?.id]);

    const handleOpenNew = () => {
        setEditing(null);
        setDescription('');
        setFileList([]);
        setModalOpen(true);
    }

    const handleSubmit = async () => {
        setIsSaving(true);

        try {
            const formData = new FormData();
            formData.append('description', description);

            const currentFile = fileList[0];
            if (currentFile?.originFileObj) {
                formData.append('file', currentFile.originFileObj);
                formData.append('file_name', currentFile.originFileObj.name);
            } else if (editing?.file_name) {
                formData.append('file_name', editing.file_name);
            }

            let response;

            if (editing) {
                response = await Api(`/reports/${report.id}/complements`).update(editing.id, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await Api(`/reports/${report.id}/complements`).store(formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            api.success({ message: 'Complemento salvo.' });
            setModalOpen(false);
            
            if (response?.data) {
                setComplements(previous => {
                    if (editing) {
                        return previous.map(item => item.id === editing.id ? response.data : item);
                    }

                    return [response.data, ...previous];
                });
            }

            fetchComplements();
        } catch (err) {
            api.error({ message: 'Erro ao salvar complemento.' });
        }

        setIsSaving(false);
    }

    const handleEdit = complement => {
        setEditing(complement);
        setDescription(complement.description || '');

        if (complement.file_url) {
            setFileList([{
                uid: complement.id,
                name: complement.file_name,
                url: complement.file_url,
                status: 'done'
            }]);
        } else {
            setFileList([]);
        }

        setModalOpen(true);
    }

    const handleDelete = async (id) => {
        try {
            await Api(`/reports/${report.id}/complements/${id}/`).delete();
            api.success({ message: 'Complemento excluído.' });
            fetchComplements();
        } catch (err) {
            api.error({ message: 'Erro ao excluir complemento.' });
        }
    }

    const canSubmit = description.trim().length > 0 && fileList.length > 0;

    const columns = [
         {
            title: 'Documento',
            key: 'file',
            width: '20%',
            render: (_, record) => {
                if (!record.file_url) return null;

                const displayName = record.file_name?.split('/').pop() || record.file_url;

                return (
                    <Tooltip title={'Visualizar Documento'}>
                        <Typography.Link href={record.file_url} target="_blank" rel="noreferrer">
                            {displayName}
                        </Typography.Link>
                    </Tooltip>
                )
            }
        },
        { title: 'Descrição', dataIndex: 'description', key: 'description', width: '40%'},
        {
            title: 'Usuário',
            key: 'user', 
            width: '20%',   
            render: (_, record) => record.user?.full_name || `${record.user?.first_name || ''} ${record.user?.last_name || ''}`.trim() || '-'
        },
       
        { title: 'Atualizado em', dataIndex: 'updated_at', key: 'updated_at', width: '10%' },
        {
            title: 'Ações',
            key: 'actions',
            width: '10%',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Editar">
                        <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}/>
                    </Tooltip>
                    <Popconfirm
                        title="Excluir Complemento?"
                        onOpenChange={open => {
                            if (open) setDeleteTooltipVisible(prev => ({ ...prev, [record.id]: false }));
                        }}
                        onConfirm={() => handleDelete(record.id)}
                        okText="Sim"
                        cancelText="Cancelar"
                    >
                        <Tooltip title="Excluir" visible={deleteTooltipVisible[record.id]} onVisibleChange={v => setDeleteTooltipVisible(prev => ({ ...prev, [record.id]: v }))}>
                            <Button type="link" danger icon={<DeleteOutlined />}/>
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ marginTop: 24 }}>
            {contextHolder}
            
            <Row justify="right" style={{ marginBottom: 12 }}>
                <Col>
                    <Typography.Title level={4} style={{ margin: 0 }}>Documentos Complementares</Typography.Title>
                    <Typography.Text type="secondary">Arquivos e documentos complementares ao RMA após sua homologação</Typography.Text>
                </Col>

            </Row>
        
            <Row justify="space-between" align="left" style={{ marginBottom: 12 }}>
                <Col></Col>
                <Col>
                    <Tooltip title="Adicionar complemento">
                        <Button type="primary" onClick={handleOpenNew}>Adicionar</Button>
                    </Tooltip>
                </Col>
            </Row>

            <Table
                dataSource={complements}
                columns={columns}
                rowKey={record => record.id}
                loading={loading}
                pagination={false}
            />

            <Modal
                title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        {editing ? 'Editar Complemento' : 'Adicionar Complemento'}
                    </Typography.Title>
                }
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={handleSubmit}
                confirmLoading={isSaving}
                okText="Salvar"
                okButtonProps={{ disabled: !canSubmit }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Row style={{ marginBottom: '1rem', marginTop: '1rem'  }}>
                        <Typography.Title level={5}>Qual a descrição?<Typography.Text type="danger"> *</Typography.Text></Typography.Title>
                        <Input.TextArea
                            showCount 
                            maxLength={400}
                            placeholder="Informe uma descrição para o complemento" 
                            value={description} 
                            onChange={({ target }) => setDescription(target.value)
                        } />
                    </Row>
                    <Row style={{ marginBottom: '1rem' }}>
                        <Typography.Title level={5}>
                            Qual o arquivo? <Typography.Text type="danger"> *</Typography.Text>
                        </Typography.Title>
                        <Col span={24}>
                        <Upload
                            accept=".pdf,application/pdf"
                            beforeUpload={(file) => {
                                const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                                if (!isPdf) {
                                    api.error({ message: 'Apenas arquivos em formato PDF são permitidos.' });
                                    // impede que o arquivo inválido vá para o fileList
                                    return Upload.LIST_IGNORE; 
                                }
                                // impede o upload automático
                                return false; 
                            }}
                            fileList={fileList}
                            onRemove={() => setFileList([])}
                            onChange={({ fileList: incoming }) => setFileList(incoming)}
                        >
                            {fileList.length === 0 && (
                                <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
                            )}
                        </Upload>
                        </Col>
                    </Row>                    
                </Space>
            </Modal>
        </div>
    )
}

export default ReportComplements;
